/**
 * Reading the party's retainers, and the options a hire needs.
 *
 * Two fetches with different lifetimes, which is why they are two hooks. The
 * retainers change whenever somebody is hired, released or levels past their
 * patron, so they are re-asked on a token. The **options** — races, classes,
 * alignments and the printed generation methods — are the corpus and never
 * change while a tab is open, so they are fetched once and only when a hire is
 * actually possible: a player who can never hire anybody should not pay for the
 * whole character wizard's option payload on every campaign load.
 */

import { useEffect, useState } from 'react';

import { api } from '../api.js';
import type { CharacterOptions } from '../characters/options.js';
import type { HenchmenView } from './HenchmenPanel.js';

export function useHenchmen(
  campaignId: string | undefined,
  /** Anything that changes when the retainer list may have moved. */
  token?: unknown,
): HenchmenView | null {
  const [view, setView] = useState<HenchmenView | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    // **A stale response must not win.** A release and the refetch it triggers
    // are two requests in flight at once, and if they resolve out of order the
    // panel settles on the older list — showing a henchman who has just left as
    // still retained until something else happens to move the token. Cheaper
    // than an AbortController and it answers the same question: this effect has
    // been superseded, so its answer is no longer the answer.
    let current = true;
    void api
      .get<HenchmenView>(`/api/campaigns/${campaignId}/henchmen`)
      // Null on failure rather than an empty view: the panel renders nothing
      // for null, and an empty one would say "no retainers" to a table that
      // has three.
      .then((body) => {
        if (current) setView(body ?? null);
      })
      .catch(() => {
        if (current) setView(null);
      });
    return () => {
      current = false;
    };
  }, [campaignId, token]);

  return view;
}

export function useCharacterOptions(enabled: boolean): CharacterOptions | null {
  const [options, setOptions] = useState<CharacterOptions | null>(null);

  useEffect(() => {
    if (!enabled || options) return;
    void api
      .get<CharacterOptions>('/api/character-options')
      .then((body) => setOptions(body ?? null))
      .catch(() => setOptions(null));
  }, [enabled, options]);

  return options;
}
